import type { UseFormReturn } from 'react-hook-form';

import { FormDateInput, FormField, FormInput, FormSelect } from '@/_components';
import {
  CardContainer,
  FormCurrencyInput,
  FormLayout,
  SectionHeading,
  SelectorWithAvatarContainer,
} from '@/_containers';
import { RichText } from '@/_containers/inputs/rich-text';
import { AsyncCompanySelector } from '@/_pages/companies/shared';
import { BrandsAsyncSelect } from '@/_pages/settings/brands/shared';
import { PartnersSelect } from '@/_pages/settings/partners/shared';
import { nameResolver, showFormField } from '@/_shared';

import type { InvoiceFormSchema } from '../schemas';
import { executionStatusOptions, invoiceLifecycleStatusOptions } from '../schemas/status-options';

export function FormFields({
  form,
  isEditable = true,
  isCreatingInvoiceNumber,
  restrictEditableFields = false,
}: {
  form: UseFormReturn<InvoiceFormSchema>;
  isEditable?: boolean;
  isCreate?: boolean;
  isCreatingInvoiceNumber?: boolean;
  restrictEditableFields?: boolean;
}) {
  const [id, notes] = form.watch(['id', 'notes']);

  const canEditStructuralFields = isEditable && !restrictEditableFields;

  return (
    <>
      <CardContainer>
        <FormLayout>
          <FormField
            control={form.control}
            name="invoice_date"
            render={({ field: { value, onChange } }) => (
              <FormDateInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Invoice Date (required)"
                value={value}
                onValueChange={onChange}
                disabled={!canEditStructuralFields}
              />
            )}
          />
          {/* <FormField
            control={form.control}
            name="billing_period_start_date"
            render={({ field: { value, onChange } }) => (
              <FormDateInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
                label="Billing Start Date"
                value={value}
                onValueChange={onChange}
                disabled={!isEditable}
              />
            )}
          />
          <FormField
            control={form.control}
            name="billing_period_end_date"
            render={({ field: { value, onChange } }) => (
              <FormDateInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
                label="Billing End Date"
                value={value}
                onValueChange={onChange}
                disabled={!isEditable}
              />
            )}
          /> */}
          <FormField
            control={form.control}
            name="due_date"
            render={({ field: { value, onChange } }) => (
              <FormDateInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Due Date"
                value={value}
                onValueChange={onChange}
                disabled={!isEditable}
              />
            )}
          />

          <div className="col-span-12 sm:col-span-6 lg:col-span-4">
            <FormField
              control={form.control}
              name="client"
              render={({ field: { value, onChange } }) => (
                <SelectorWithAvatarContainer
                  disabled={!canEditStructuralFields}
                  src={value?.logo?.at(0)?.url || ''}
                  label={value?.name || 'Select Company'}
                  name={value?.name ?? ''}
                  className="lg:flex-row"
                  avatarClassName="w-14 rounded-md"
                >
                  <AsyncCompanySelector
                    disabled={!canEditStructuralFields}
                    label="Company (required)"
                    value={value}
                    onValueChange={onChange}
                  />
                </SelectorWithAvatarContainer>
              )}
            />
          </div>

          <div className="col-span-12 sm:col-span-6 lg:col-span-4">
            <FormField
              control={form.control}
              name="brand"
              render={({ field: { value, onChange } }) => (
                <SelectorWithAvatarContainer
                  disabled={!canEditStructuralFields}
                  src={value?.icon?.at(0)?.url || ''}
                  label={nameResolver(value?.name, undefined, 'Brand')}
                  name={value?.name ?? ''}
                  className="lg:flex-row"
                >
                  <BrandsAsyncSelect
                    disabled={!canEditStructuralFields}
                    label="Brand"
                    value={value}
                    onValueChange={onChange}
                  />
                </SelectorWithAvatarContainer>
              )}
            />
          </div>

          <div className="col-span-12 sm:col-span-6 lg:col-span-4">
            <FormField
              control={form.control}
              name="partner"
              render={({ field: { value, onChange } }) => (
                <SelectorWithAvatarContainer
                  disabled={!canEditStructuralFields}
                  src={value?.icon?.at(0)?.url || ''}
                  label={nameResolver(value?.name, undefined, 'Partner')}
                  name={value?.name ?? ''}
                  className="lg:flex-row"
                >
                  <PartnersSelect
                    disabled={!canEditStructuralFields}
                    label="Partner"
                    value={value}
                    onValueChange={onChange}
                  />
                </SelectorWithAvatarContainer>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="execution_status"
            render={({ field: { value, onChange } }) => (
              <FormSelect
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Execution status"
                value={executionStatusOptions.find((option) => option.value === value) ?? null}
                onChange={(option) => onChange(option?.value ?? null)}
                options={executionStatusOptions}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.value}
                disabled={!isEditable}
              />
            )}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field: { value, onChange } }) => (
              <FormSelect
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Status"
                value={invoiceLifecycleStatusOptions.find((option) => option.value === value) ?? null}
                onChange={(option) => onChange(option?.value ?? null)}
                options={invoiceLifecycleStatusOptions}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.value}
                disabled={!isEditable}
              />
            )}
          />

          <FormField
            control={form.control}
            name="invoice_number"
            render={({ field: { value, onChange } }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Invoice #"
                disabled
                value={isCreatingInvoiceNumber ? 'Loading...' : (value ?? '')}
                onChange={onChange}
              />
            )}
          />

          <FormCurrencyInput
            isEditable={canEditStructuralFields}
            name="invoice_amount"
            formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
            label="Invoice Amount"
            className="font-bold"
          />
        </FormLayout>
      </CardContainer>

      {id !== -1 && showFormField(isEditable, !!notes?.length) && (
        <CardContainer>
          <SectionHeading text="Notes" />
          <FormLayout>
            <FormField
              control={form.control}
              name="notes"
              render={({ field: { value, onChange } }) => (
                <RichText
                  value={value || ''}
                  onChange={onChange}
                  disabled={!isEditable}
                  inline={!isEditable}
                  mediaFolderPath={`invoices/${id}/notes`}
                  className="col-span-12"
                />
              )}
            />
          </FormLayout>
        </CardContainer>
      )}
    </>
  );
}
